/**
 * Terminal column width for a code point (a compact wcwidth).
 *
 * Terminals disagree about a handful of ranges — most notably the "ambiguous
 * width" East Asian block and emoji — so the editor treats width as advice and
 * surfaces the risky cases as warnings rather than silently reflowing a design.
 */

type Range = readonly [number, number];

/** Zero-width: combining marks, variation selectors, joiners. */
const ZERO_WIDTH: readonly Range[] = [
  [0x0300, 0x036f], [0x0483, 0x0489], [0x0591, 0x05bd], [0x05bf, 0x05bf],
  [0x05c1, 0x05c2], [0x05c4, 0x05c5], [0x0610, 0x061a], [0x064b, 0x065f],
  [0x0670, 0x0670], [0x06d6, 0x06dc], [0x06df, 0x06e4], [0x0711, 0x0711],
  [0x0730, 0x074a], [0x07a6, 0x07b0], [0x0816, 0x0819], [0x08e3, 0x0903],
  [0x093a, 0x093c], [0x0941, 0x0948], [0x094d, 0x094d], [0x0951, 0x0957],
  [0x0e31, 0x0e31], [0x0e34, 0x0e3a], [0x0e47, 0x0e4e],
  [0x1ab0, 0x1aff], [0x1dc0, 0x1dff], [0x200b, 0x200f], [0x2028, 0x202e],
  [0x2060, 0x2064], [0x20d0, 0x20f0], [0xfe00, 0xfe0f], [0xfe20, 0xfe2f],
  [0xfeff, 0xfeff], [0xe0100, 0xe01ef],
];

/** East Asian Wide / Fullwidth: two columns in every mainstream terminal. */
const WIDE: readonly Range[] = [
  [0x1100, 0x115f], [0x2e80, 0x303e], [0x3041, 0x33ff], [0x3400, 0x4dbf],
  [0x4e00, 0x9fff], [0xa000, 0xa4cf], [0xa960, 0xa97f], [0xac00, 0xd7a3],
  [0xf900, 0xfaff], [0xfe10, 0xfe19], [0xfe30, 0xfe6f], [0xff00, 0xff60],
  [0xffe0, 0xffe6], [0x1f300, 0x1f64f], [0x1f900, 0x1f9ff],
  [0x20000, 0x2fffd], [0x30000, 0x3fffd],
];

/** East Asian Ambiguous: one column in a Latin terminal, two in a CJK one. */
const AMBIGUOUS: readonly Range[] = [
  [0x00a1, 0x00a1], [0x00a4, 0x00a4], [0x00a7, 0x00a8], [0x00aa, 0x00aa],
  [0x00b0, 0x00b4], [0x00b6, 0x00ba], [0x00bc, 0x00bf], [0x00c6, 0x00c6],
  [0x00d0, 0x00d0], [0x00d7, 0x00d8], [0x00e0, 0x00e1], [0x00e8, 0x00ea],
  [0x2010, 0x2010], [0x2013, 0x2016], [0x2018, 0x2019], [0x201c, 0x201d],
  [0x2020, 0x2022], [0x2024, 0x2027], [0x2030, 0x2030], [0x2032, 0x2033],
  [0x2160, 0x216b], [0x2170, 0x2179], [0x2190, 0x2199], [0x21d2, 0x21d2],
  [0x2200, 0x22bf], [0x2460, 0x24ea], [0x25a0, 0x25ff], [0x2605, 0x2606],
  [0x2609, 0x2609], [0x260e, 0x260f], [0x261c, 0x261e], [0x2640, 0x2642],
  [0x2660, 0x2665], [0x2776, 0x277f],
];

function inRanges(cp: number, ranges: readonly Range[]): boolean {
  let lo = 0;
  let hi = ranges.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const [start, end] = ranges[mid];
    if (cp < start) hi = mid - 1;
    else if (cp > end) lo = mid + 1;
    else return true;
  }
  return false;
}

/** Columns occupied by a single code point. Control characters report 0. */
export function codePointWidth(cp: number): 0 | 1 | 2 {
  if (cp === 0) return 0;
  if (cp < 32 || (cp >= 0x7f && cp < 0xa0)) return 0;
  if (inRanges(cp, ZERO_WIDTH)) return 0;
  if (inRanges(cp, WIDE)) return 2;
  return 1;
}

/**
 * Columns occupied by a grapheme cluster: the base character's width, with
 * combining marks contributing nothing.
 */
export function charWidth(cluster: string): 0 | 1 | 2 {
  if (cluster === '') return 0;
  let width: 0 | 1 | 2 = 0;
  for (const ch of cluster) {
    const w = codePointWidth(ch.codePointAt(0) ?? 0);
    if (w > width) width = w;
  }
  return width;
}

export function stringWidth(s: string): number {
  let total = 0;
  for (const ch of s) total += codePointWidth(ch.codePointAt(0) ?? 0);
  return total;
}

export type WidthRisk = 'safe' | 'ambiguous' | 'wide' | 'zero' | 'control';

export interface WidthWarning {
  char: string;
  codePoint: number;
  risk: WidthRisk;
  message: string;
}

/** Classifies a cluster for the compatibility checker. */
export function analyzeChar(cluster: string): WidthWarning | null {
  if (cluster === '' || cluster === ' ') return null;
  const cp = cluster.codePointAt(0) ?? 0;
  if (cp < 32 || (cp >= 0x7f && cp < 0xa0)) {
    return {
      char: cluster,
      codePoint: cp,
      risk: 'control',
      message: 'Control character — will not render as a glyph.',
    };
  }
  if (inRanges(cp, ZERO_WIDTH)) {
    return {
      char: cluster,
      codePoint: cp,
      risk: 'zero',
      message: 'Zero-width character — occupies no cell of its own.',
    };
  }
  if (inRanges(cp, WIDE)) {
    return {
      char: cluster,
      codePoint: cp,
      risk: 'wide',
      message: 'Double-width character — occupies 2 terminal cells.',
    };
  }
  if (inRanges(cp, AMBIGUOUS)) {
    return {
      char: cluster,
      codePoint: cp,
      risk: 'ambiguous',
      message: 'Ambiguous width — may occupy 2 cells in CJK terminal locales.',
    };
  }
  if (cp > 0xffff) {
    return {
      char: cluster,
      codePoint: cp,
      risk: 'ambiguous',
      message: 'Astral-plane character — width and font support vary by terminal.',
    };
  }
  return null;
}
